import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Camera, Upload, X } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface PhotoUploadStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

export const PhotoUploadStep = ({
	onNext,
	onBack,
	initialData,
}: PhotoUploadStepProps) => {
	const [photoUrl, setPhotoUrl] = useState(initialData?.photoUrl || "");
	const [uploading, setUploading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [uploadSuccess, setUploadSuccess] = useState(false);
	const [uploadProgress, setUploadProgress] = useState("");

	const handleFileUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setUploading(true);
		setUploadProgress("Preparing photo...");

		try {
			// Convert file to base64
			const reader = new FileReader();
			reader.onload = async () => {
				try {
					setUploadProgress("Converting photo...");
					const base64Data = (reader.result as string).split(",")[1]; // Remove data:image/...;base64, prefix

					setUploadProgress("Uploading to server...");
					const response = await fetch(
						`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-profile-photo`,
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
							},
							body: JSON.stringify({
								file: base64Data,
								filename: file.name,
								content_type: file.type,
							}),
						},
					);

					const result = await response.json();

					if (!response.ok) {
						throw new Error(result.error || "Failed to upload photo");
					}

					setUploadProgress("Processing...");
					setPhotoUrl(result.data.public_url);

					setUploadProgress("Complete!");

					// Show success feedback
					setUploadSuccess(true);

					// Hide success feedback after 3 seconds
					setTimeout(() => {
						setUploadSuccess(false);
						setUploadProgress("");
					}, 3000);
				} catch (error) {
					console.error("Error uploading file:", error);
					alert("Error uploading file. Please try again.");
				} finally {
					setUploading(false);
				}
			};
			reader.readAsDataURL(file);
		} catch (error) {
			console.error("Error reading file:", error);
			alert("Error reading file. Please try again.");
			setUploading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!photoUrl) {
			alert("Please upload a profile photo");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "photo",
						data: {
							photo: {
								profile_photo_url: photoUrl,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save photo");
			}

			onNext({ photoUrl });
		} catch (error) {
			console.error("Error saving photo:", error);
			alert("Failed to save photo. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 8 of 15</span>
					<span className={styles.progressLabel}>Profile Photo</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "50%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Camera className={styles.cardIcon} />
						Profile Photo
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Upload a professional photo that families will see when viewing your
						profile.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Photo Upload Area */}
						<div className="flex justify-center">
							<div className="w-48 h-48 border-2 border-dashed border-newTheme-spruce/40 rounded-lg flex flex-col items-center justify-center bg-newTheme-white relative">
								{photoUrl ? (
									<>
										<img
											src={photoUrl}
											alt="Profile"
											className="w-full h-full object-cover rounded-lg"
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => setPhotoUrl("")}
											className="absolute top-2 right-2 bg-red-500 text-white hover:bg-red-600"
										>
											<X className="h-4 w-4" />
										</Button>
									</>
								) : (
									<div className="text-center">
										<Upload className="h-12 w-12 text-newTheme-spruce/50 mx-auto mb-2" />
										<p className="text-sm text-newTheme-black/60 font-karla">
											Upload Photo
										</p>
									</div>
								)}
							</div>
						</div>

						{/* Upload Button */}
						<div className="flex justify-center">
							<Button
								type="button"
								variant="outline"
								onClick={() => document.getElementById("photo-upload")?.click()}
								disabled={uploading}
								className={styles.uploadBtn}
							>
								{uploading ? (
									<div className="flex items-center">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-newTheme-spruce mr-2" />
										{uploadProgress || "Uploading..."}
									</div>
								) : (
									<>
										<Upload className="h-4 w-4 mr-2" />
										Choose Photo
									</>
								)}
							</Button>
							<input
								id="photo-upload"
								type="file"
								accept="image/*"
								onChange={handleFileUpload}
								className="hidden"
							/>
						</div>

						{/* Success feedback */}
						{uploadSuccess && (
							<div className="flex justify-center">
								<div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 animate-bounce">
									<svg
										className="h-5 w-5 text-green-600"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-green-700 font-medium font-karla">
										Photo uploaded successfully!
									</span>
								</div>
							</div>
						)}

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={!photoUrl || loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
